import {
    ApiOutlined,
    AppstoreOutlined,
    BankOutlined,
    BarChartOutlined,
    CreditCardOutlined,
    InfoCircleOutlined,
    LockOutlined,
    MailOutlined,
    TeamOutlined,
    ThunderboltOutlined,
    WalletOutlined,
} from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { PageLayout } from '@client/components/_core/layout/PageLayout';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { modal } from '@client/components/_ui/feedback/modal';
import { RequestSupportSessionDrawer } from '@client/components/admin-console/support-session/RequestSupportSessionDrawer';
import { DomainAuthSetup } from '@client/components/domain-auth/DomainAuthSetup';
import { EmailVerificationStatusPanel } from '@client/components/domain-auth/EmailVerificationStatusPanel';
import { getEmailVerificationStatus } from '@client/components/domain-auth/emailVerificationStatus';
import { InboundReplyToField } from '@client/components/domain-auth/InboundReplyToField';
import { NoReplyEmailField } from '@client/components/domain-auth/NoReplyEmailField';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import type { RouterOutputs } from '@client/trpc/client';
import type { OnFinishFailedFn } from '@client/types/form';
import type { ShareableRouteContext } from '@client/types/router';
import { generateAdminConsentUrl } from '@client/utils/auth/azureAD';
import {
    APP_MODULES,
    type AppModule,
    AUTH0_CONNECTIONS,
    IANA_TIMEZONE,
    isValidInboundEmailId,
    SYSTEM_ORG_IDS,
} from '@shared/constants';
import { McpAccessSetting } from '@shared/prisma/enums';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { Alert, Card, Checkbox, Dropdown, Form, Input, Select, Skeleton, Switch, Tabs, Typography } from 'antd';
import {
    AlertTriangle,
    BookOpen,
    Building2,
    Copy,
    Download,
    Headphones,
    Link2,
    MessageSquare,
    Shield,
    ShieldAlert,
    Upload,
    Users,
} from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import invariant from 'tiny-invariant';
import { ImportComplaintsModal } from './~imports/ImportComplaintsModal';
import { ImportControlActivitiesModal } from './~imports/ImportControlActivitiesModal';
import { ImportControlsModal } from './~imports/ImportControlsModal';
import { ImportControlTestsModal } from './~imports/ImportControlTestsModal';
import { ImportHandlingPartiesModal } from './~imports/ImportHandlingPartiesModal';
import { ImportIncidentsModal } from './~imports/ImportIncidentsModal';
import { ImportRiskAppetiteStatementsModal } from './~imports/ImportRiskAppetiteStatementsModal';
import { ImportRisksModal } from './~imports/ImportRisksModal';
import { ImportUsersModal } from './~imports/ImportUsersModal';
import { ImportTrainingRecordsModal } from './~training-records/ImportTrainingRecordsModal';
import { DigestSettingsSection } from './DigestSettingsSection';
import { EnabledModulesField } from './EnabledModulesField';
import { IntegrationsSection } from './IntegrationsSection';
import { OrgStatsSection } from './OrgStatsSection';
import { SendTestEmailButton } from './SendTestEmailButton';

const { Text, Paragraph } = Typography;

export const Route = createFileRoute('/admin-console/organisation-management/organisations/$orgId')({
    params: {
        parse({ orgId }) {
            return { orgId: BigInt(orgId) };
        },
    },
    staticData: {
        presentation: 'fullPage',
        permissions: ['admin_orgs:read'],
        ui: {
            icon: Building2,
            widthVariant: 'default',
        },
    },
    beforeLoad({ context: { trpcUtils }, params }): ShareableRouteContext {
        // Block access to system console organization
        if (params.orgId === SYSTEM_ORG_IDS.SYSTEM_CONSOLE) {
            throw redirect({ to: '/admin-console/organisation-management/organisations' });
        }

        async function title() {
            const org = await trpcUtils.admin.org.getOrg.ensureData({ orgId: params.orgId });
            return org?.name || '';
        }

        return { breadcrumb: { title }, ui: { title, subtitle: 'Manage organisation settings and users' } };
    },
    component: OrgForm,
});

type Org = Pick<
    NonNullable<RouterOutputs['admin']['org']['getOrg']>,
    | 'name'
    | 'enabledModules'
    | 'enabledAuth0Connections'
    | 'emailId'
    | 'isEnabled'
    | 'stripeId'
    | 'maxAllowedUsers'
    | 'isInitialized'
    | 'restrictions'
    | 'reportTimezone'
    | 'senderEmail'
    | 'noReplyEmail'
    | 'lastLoginAt'
    | 'createdAt'
    | 'platformReplyEmail'
    | 'authenticatedDomain'
>;

const FormItem = Form.Item<Org>;

const moduleOptions: { label: string; value: AppModule }[] = [
    { label: 'Claims module', value: APP_MODULES.CLAIM },
    { label: 'Compliance module', value: APP_MODULES.COMPLIANCE },
    { label: 'Risk module', value: APP_MODULES.RISK },
    { label: 'Expenses and billing', value: APP_MODULES.BILLING },
    { label: 'Material service provider register', value: APP_MODULES.MSPR },
    { label: 'Attestations module', value: APP_MODULES.ATTESTATIONS },
    { label: 'Fleet module', value: APP_MODULES.FLEET },
];

const toolOptions: { label: string; value: AppModule }[] = [
    { label: 'Auto-detect', value: APP_MODULES.AUTO_DETECT },
    { label: 'Compliance audit', value: APP_MODULES.COMPLIANCE_AUDIT },
    { label: 'Document analyser', value: APP_MODULES.DOCUMENT_ANALYZER },
    { label: 'Website checker', value: APP_MODULES.WEBSITE_CHECKER },
    { label: 'PDS compare', value: APP_MODULES.PDS_COMPARE },
];

function OrgForm() {
    const formId = useId();
    const { orgId } = Route.useParams();
    const [form] = Form.useForm<Org>();
    const { trpc, trpcClient, trpcUtils } = useTrpc();
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('basic');
    const [tenantDomain, setTenantDomain] = useState('');
    const [generatedUrl, setGeneratedUrl] = useState('');
    const [showConsentUrl, setShowConsentUrl] = useState(false);
    // Auto-detect is now enabled via the `auto-detect` tool key in "Enabled Tools" (no standalone
    // toggle). The email-ID warning/disabled logic below derives from that key.
    const enabledModulesValue = Form.useWatch('enabledModules', form);
    const isAutoDetectEnabled = enabledModulesValue?.includes(APP_MODULES.AUTO_DETECT) ?? false;
    const emailIdValue = Form.useWatch('emailId', form);
    const watchedSenderEmail = Form.useWatch('senderEmail', form);
    const showEmailIdWarning = isAutoDetectEnabled && !emailIdValue?.trim();

    // Import modal state - union type to ensure only one modal open at a time
    type ImportModalType =
        | 'users'
        | 'handlingParties'
        | 'trainingRecords'
        | 'incidents'
        | 'complaints'
        | 'risks'
        | 'riskAppetiteStatements'
        | 'controls'
        | 'controlTests'
        | 'controlActivities'
        | null;
    const [openImportModal, setOpenImportModal] = useState<ImportModalType>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [supportSessionOpen, setSupportSessionOpen] = useState(false);
    const { canEdit: canManageOrganisations } = useAdminModulePermissions('orgs');
    const { canEdit: canManageSupport } = useAdminModulePermissions('support');
    const { hasAccess: hasContentAccess } = useAdminModulePermissions('content');

    const { data, isPending, refetch } = trpc.admin.org.getOrg.useQuery({ orgId }, {});
    const { mutateAsync: verifyOrgEmailDomain, isPending: isVerifyingEmailDomain } =
        trpc.admin.org.verifyOrgEmailDomain.useMutation();
    const { mutate: updateOrg } = trpc.admin.org.updateOrg.useMutation({
        onError: (error) => {
            errorMessage.show(error);
        },
    });
    const { mutate: setMcpOverride, isPending: isSavingMcpOverride } = trpc.admin.org.setMcpOverride.useMutation({
        onSuccess: () => {
            message.success('MCP access override updated.');
            void trpcUtils.admin.org.invalidate();
        },
        onError: (error) => {
            errorMessage.show(error);
        },
    });
    const { mutate: setServiceApiEnabled, isPending: isSavingServiceApiEnabled } = trpc.admin.org.updateOrg.useMutation(
        {
            onSuccess: () => {
                message.success('Service API access updated.');
                void trpcUtils.admin.org.invalidate();
            },
            onError: (error) => {
                errorMessage.show(error);
            },
        },
    );
    const { mutate: setLiteOrg, isPending: isSavingLiteOrg } = trpc.admin.org.updateOrg.useMutation({
        onSuccess: () => {
            message.success('Curium Lite setting updated.');
            void trpcUtils.admin.org.invalidate();
        },
        onError: (error) => {
            errorMessage.show(error);
        },
    });
    const { mutate: setDemoOrg, isPending: isSavingDemoOrg } = trpc.admin.demoOrg.setDemoOrg.useMutation({
        onSuccess: ({ isDemoOrg }) => {
            message.success(isDemoOrg ? 'Marked as a demo org.' : 'Unmarked as a demo org.');
            void trpcUtils.admin.org.invalidate();
        },
        onError: (error) => {
            errorMessage.show(error);
        },
    });

    useEffect(() => {
        if (data) {
            form.setFieldsValue(data);
        }
    }, [data?.id]);

    useEffect(() => {
        form.setFieldValue('authenticatedDomain', data?.authenticatedDomain ?? undefined);
    }, [data?.authenticatedDomain, form]);

    const emailVerificationStatus = getEmailVerificationStatus(
        data?.senderEmail,
        data?.isEmailDomainVerified,
        data?.authenticatedDomain,
    );

    async function handleRecheckEmailVerification() {
        try {
            const result = await verifyOrgEmailDomain({ orgId });

            if (result.verified) {
                message.success(result.message);
            } else {
                message.warning(result.message);
            }
        } catch (error) {
            errorMessage.show(error);
        } finally {
            await refetch();
        }
    }

    const onSave = async (values: Org, options?: { allowEmailIdTransfer?: boolean }) => {
        setSaving(true);
        message.loading('Saving...');
        invariant(orgId, 'orgId is required');

        updateOrg(
            {
                orgId,
                // Released-address tombstone override (ai-inbox-self-service S3) — only ever set
                // through the explicit confirm dialog below, never on a first save attempt.
                ...(options?.allowEmailIdTransfer ? { allowEmailIdTransfer: true } : {}),
                set: {
                    name: values.name,
                    isEnabled: values.isEnabled,
                    enabledModules: values.enabledModules,
                    enabledAuth0Connections: values.enabledAuth0Connections,
                    emailId: values.emailId?.trim() || null,
                    stripeId: values.stripeId,
                    maxAllowedUsers: values.maxAllowedUsers,
                    isInitialized: values.isInitialized,
                    restrictions: values.restrictions,
                    reportTimezone: values.reportTimezone ?? undefined,
                    senderEmail: values.senderEmail ?? undefined,
                    noReplyEmail: values.noReplyEmail ?? undefined,
                    platformReplyEmail: values.platformReplyEmail?.trim() || null,
                    // authenticatedDomain is not writable via admin updateOrg — it flows from
                    // the domain-authentication flow only. The form field below is read-only.
                },
            },
            {
                onSuccess: (result) => {
                    const replyTo = values.platformReplyEmail?.trim() || null;
                    if (result.inboundParseAutoCreated && replyTo) {
                        const hostname = replyTo.split('@')[1];
                        // Refresh the inline availability warning — the cached domains list
                        // from before the save won't include this newly-provisioned hostname,
                        // so the warning would otherwise keep showing for up to 60s after success.
                        void trpcUtils.admin.inboundParse.listAvailableDomains.invalidate();
                        message.success(
                            `Organisation settings saved. Inbound parse setting created for ${hostname}. Add the MX record to mx.sendgrid.net (priority 10) on this subdomain to finish setup.`,
                            10,
                        );
                    } else if (result.inboundParseWarning) {
                        message.warning(result.inboundParseWarning, 8);
                    } else {
                        message.success('Organisation settings saved successfully.');
                    }
                    void trpcUtils.admin.org.invalidate();
                    setSaving(false);
                },
                onError: (error) => {
                    // The address was previously held by ANOTHER org (tombstone, S3). Offer the
                    // admin-only transfer override — an audited, deliberate second step.
                    if (error.data?.errorId === 'org.emailIdPreviouslyHeld' && !options?.allowEmailIdTransfer) {
                        message.destroy();
                        setSaving(false);
                        modal.confirm({
                            title: 'Transfer a previously used email ID?',
                            content:
                                "This email ID was previously used by another organisation and is reserved to it — mail their staff still auto-forward would land in this organisation's inbox. Only transfer it if you are certain the address genuinely belongs to this organisation now. The transfer is recorded in the audit trail.",
                            okText: 'Transfer address',
                            okButtonProps: { danger: true },
                            onOk: () => onSave(values, { allowEmailIdTransfer: true }),
                        });
                        return;
                    }
                    errorMessage.show(error);
                    setSaving(false);
                },
            },
        );
    };

    const onFinishFailed: OnFinishFailedFn<Org> = (errorInfo) => {
        errorMessage.validation(errorInfo.errorFields.map((field) => field.errors));
    };

    const handleGenerateConsentUrl = () => {
        if (!tenantDomain) {
            message.warning('Please enter a tenant domain');
            return;
        }

        const url = generateAdminConsentUrl(tenantDomain, orgId, data?.name || 'Organisation');
        setGeneratedUrl(url);
        setShowConsentUrl(true);
    };

    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(generatedUrl);
            message.success('Admin consent URL copied to clipboard');
        } catch {
            message.error('Failed to copy URL. Please select and copy manually.');
        }
    };

    // Import dropdown menu items
    const importMenuItems = [
        {
            key: 'users',
            icon: <Users size={16} />,
            label: 'Users',
            onClick: () => setOpenImportModal('users'),
        },
        {
            key: 'handlingParties',
            icon: <Building2 size={16} />,
            label: 'Handling Parties',
            onClick: () => setOpenImportModal('handlingParties'),
        },
        { type: 'divider' as const },
        {
            key: 'trainingRecords',
            icon: <BookOpen size={16} />,
            label: 'Training Records',
            onClick: () => setOpenImportModal('trainingRecords'),
        },
        {
            key: 'incidents',
            icon: <AlertTriangle size={16} />,
            label: 'Incidents',
            onClick: () => setOpenImportModal('incidents'),
        },
        {
            key: 'complaints',
            icon: <MessageSquare size={16} />,
            label: 'Complaints',
            onClick: () => setOpenImportModal('complaints'),
        },
        {
            key: 'risks-group',
            icon: <ShieldAlert size={16} />,
            label: 'Risks',
            children: [
                { key: 'risks', label: 'Risk register', onClick: () => setOpenImportModal('risks') },
                {
                    key: 'riskAppetiteStatements',
                    label: 'Risk appetite statements',
                    onClick: () => setOpenImportModal('riskAppetiteStatements'),
                },
            ],
        },
        { type: 'divider' as const },
        {
            key: 'controls-group',
            icon: <Shield size={16} />,
            label: 'Controls',
            children: [
                { key: 'controls', label: 'Controls', onClick: () => setOpenImportModal('controls') },
                {
                    key: 'controlActivities',
                    label: 'Activities',
                    onClick: () => setOpenImportModal('controlActivities'),
                },
                { key: 'controlTests', label: 'Tests', onClick: () => setOpenImportModal('controlTests') },
            ],
        },
    ];

    const handleExportObligations = async () => {
        try {
            setIsExporting(true);
            message.loading('Exporting obligations...');

            const result = await trpcClient.admin.obligation.exportOrgObligations.query({ orgId });

            const blob = new Blob(['\uFEFF' + result.csv], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = result.filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            message.destroy();
            message.success(`Exported ${result.count} obligations`);
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportControls = async () => {
        try {
            setIsExporting(true);
            message.loading('Exporting controls...');

            const result = await trpcClient.admin.control.exportOrgControls.query({ orgId });

            const blob = new Blob(['\uFEFF' + result.csv], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = result.filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            message.destroy();
            message.success(`Exported ${result.count} controls`);
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setIsExporting(false);
        }
    };

    const exportMenuItems = [
        {
            key: 'obligations',
            icon: <Download size={16} />,
            label: 'Obligations',
            onClick: handleExportObligations,
        },
        // The export procedure requires the content admin module (the page itself
        // only needs admin_orgs:read) — only show the action to users who can
        // actually call it.
        ...(hasContentAccess
            ? [
                  {
                      key: 'controls',
                      icon: <Download size={16} />,
                      label: 'Controls',
                      onClick: handleExportControls,
                  },
              ]
            : []),
    ];

    if (isPending) {
        return <Skeleton active />;
    }

    return (
        <PageController route={Route}>
            <Outlet />
            <RequestSupportSessionDrawer
                open={supportSessionOpen}
                onClose={() => setSupportSessionOpen(false)}
                orgId={orgId}
                orgName={data?.name ?? ''}
            />
            <Form<Org>
                layout="vertical"
                form={form}
                onFinish={onSave}
                onFinishFailed={onFinishFailed}
                className="space-y-6"
                id={formId}
            >
                <PageLayout.Header
                    rightAddon={
                        <>
                            <Button.Link
                                to="/admin-console/organisation-management/organisations/$orgId/users"
                                params={{ orgId }}
                                variant="secondary"
                                icon={<TeamOutlined />}
                            >
                                Users
                            </Button.Link>
                            <Button.Link
                                to="/admin-console/organisation-management/organisations/$orgId/wallet"
                                params={{ orgId }}
                                variant="secondary"
                                icon={<WalletOutlined />}
                            >
                                Wallet
                            </Button.Link>
                            <Button.Link
                                to="/admin-console/organisation-management/organisations/$orgId/subscriptions"
                                params={{ orgId }}
                                variant="secondary"
                                icon={<CreditCardOutlined />}
                            >
                                Subscriptions
                            </Button.Link>
                            <Dropdown menu={{ items: exportMenuItems }} trigger={['click']}>
                                <Button type="secondary" icon={<Upload />} loading={isExporting}>
                                    Export data
                                </Button>
                            </Dropdown>
                            <Dropdown menu={{ items: importMenuItems }} trigger={['click']}>
                                <Button type="secondary" icon={<Download />}>
                                    Import Data
                                </Button>
                            </Dropdown>
                            {canManageSupport && (
                                <Button
                                    type="secondary"
                                    icon={<Headphones />}
                                    onClick={() => setSupportSessionOpen(true)}
                                >
                                    Request support session
                                </Button>
                            )}
                        </>
                    }
                    primaryActionAddon={
                        <Button type="primary" form={formId} htmlType="submit" loading={saving}>
                            Save Changes
                        </Button>
                    }
                />

                {!data?.isEnabled && (
                    <Alert
                        title="Organisation Inactive"
                        description="This organisation is currently inactive. Users cannot access the platform until it is activated."
                        type="warning"
                        showIcon
                        className="mb-6"
                    />
                )}

                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: 'basic',
                            icon: <BankOutlined />,
                            label: 'Basic Information',
                            children: (
                                <Card>
                                    <div className="grid gap-6 md:grid-cols-2">
                                        <FormItem
                                            label="Organisation Name"
                                            required
                                            name="name"
                                            rules={[
                                                { required: true, message: 'Please enter the organisation name' },
                                                { min: 2, message: 'Name must be at least 2 characters' },
                                            ]}
                                            tooltip={{
                                                title: 'The display name of the organisation',
                                                icon: <InfoCircleOutlined />,
                                            }}
                                        >
                                            <Input placeholder="Enter organisation name" />
                                        </FormItem>
                                        <FormItem
                                            name="emailId"
                                            label="Organisation email ID"
                                            tooltip={{
                                                title: 'The email local name specific to this organisation',
                                                icon: <InfoCircleOutlined />,
                                            }}
                                            rules={[
                                                {
                                                    // H1: byte-identical to the server boundary
                                                    // (`inboundEmailIdInputSchema`), which trims +
                                                    // lower-cases before validating — so validate
                                                    // the same normalised value here instead of a
                                                    // looser charset the save would then reject.
                                                    validator: (_, value) => {
                                                        if (
                                                            value === undefined ||
                                                            value === null ||
                                                            String(value).trim() === ''
                                                        ) {
                                                            return Promise.resolve();
                                                        }
                                                        const normalized = String(value).trim().toLowerCase();
                                                        if (!isValidInboundEmailId(normalized)) {
                                                            return Promise.reject(
                                                                new Error(
                                                                    'Must be a valid email local part: letters, numbers, and single . _ - separators (max 64 characters).',
                                                                ),
                                                            );
                                                        }
                                                        return Promise.resolve();
                                                    },
                                                },
                                            ]}
                                            help={
                                                isAutoDetectEnabled && showEmailIdWarning
                                                    ? 'Auto-detect is enabled but no email ID is set. AI inbox emails will not be routed until an email ID is configured.'
                                                    : "The local part for this organisation's Curium email addresses — when a system inbound domain is configured, the managed inbound reply-to (e.g. acme@<inbound domain>) and, when Auto-detect is on, the AI inbox address. Leave blank to fall back to a stable org-<id> identifier."
                                            }
                                            {...(showEmailIdWarning && { validateStatus: 'warning' })}
                                        >
                                            <Input placeholder="Enter email ID, e.g. contoso" allowClear />
                                        </FormItem>
                                        <FormItem
                                            label="Stripe ID"
                                            name="stripeId"
                                            tooltip={{
                                                title: 'The Stripe identifier for billing purposes',
                                                icon: <InfoCircleOutlined />,
                                            }}
                                        >
                                            <Input placeholder="Enter Stripe ID" />
                                        </FormItem>
                                        <FormItem
                                            label="Organisation is active"
                                            name="isEnabled"
                                            valuePropName="checked"
                                            tooltip={{
                                                title: 'Disable to temporarily suspend organisation access',
                                                icon: <InfoCircleOutlined />,
                                            }}
                                        >
                                            <Checkbox />
                                        </FormItem>
                                        <FormItem
                                            label="Max Allowed Users"
                                            name="maxAllowedUsers"
                                            tooltip={{
                                                title: 'Maximum number of users allowed in the organisation (set to -1 for unlimited)',
                                                icon: <InfoCircleOutlined />,
                                            }}
                                            help="Enter -1 for unlimited users"
                                        >
                                            <Input type="number" placeholder="Enter max users (-1 for unlimited)" />
                                        </FormItem>
                                        <FormItem
                                            label="Organisation is initialized"
                                            name="isInitialized"
                                            valuePropName="checked"
                                            tooltip={{
                                                title: 'Indicates if the organisation has completed initial setup',
                                                icon: <InfoCircleOutlined />,
                                            }}
                                        >
                                            <Checkbox />
                                        </FormItem>
                                        <FormItem
                                            label="Last Login"
                                            tooltip={{
                                                title: 'The last time any user logged into this organisation',
                                                icon: <InfoCircleOutlined />,
                                            }}
                                        >
                                            <Input
                                                value={
                                                    data?.lastLoginAt
                                                        ? new Date(data.lastLoginAt).toLocaleString()
                                                        : 'Never'
                                                }
                                                readOnly
                                                className="bg-muted"
                                            />
                                        </FormItem>
                                        <FormItem
                                            label="Created At"
                                            tooltip={{
                                                title: 'When this organisation was created',
                                                icon: <InfoCircleOutlined />,
                                            }}
                                        >
                                            <Input
                                                value={
                                                    data?.createdAt
                                                        ? new Date(data.createdAt).toLocaleString()
                                                        : 'Unknown'
                                                }
                                                readOnly
                                                className="bg-muted"
                                            />
                                        </FormItem>
                                        <FormItem
                                            name="restrictions"
                                            label="Restrictions"
                                            tooltip={{
                                                title: 'Special restrictions applied to this organisation',
                                                icon: <InfoCircleOutlined />,
                                            }}
                                        >
                                            <Select
                                                mode="multiple"
                                                placeholder="Select restrictions"
                                                allowClear
                                                className="w-full"
                                                options={[
                                                    {
                                                        label: (
                                                            <span className="flex items-center gap-2">
                                                                <LockOutlined />
                                                                Payment Required for Login
                                                            </span>
                                                        ),
                                                        value: 'payment_required_for_login',
                                                    },
                                                ]}
                                            />
                                        </FormItem>
                                        <FormItem
                                            label="Reporting timezone"
                                            name="reportTimezone"
                                            tooltip={{
                                                title: "The organisation's timezone for report date bucketing, scheduled reports, and anchoring relative dates in AI suggestions.",
                                                icon: <InfoCircleOutlined />,
                                            }}
                                            help="Defaults to Australia/Sydney. Used to bucket report dates and to resolve relative dates (e.g. 'yesterday') in AI checklist suggestions."
                                        >
                                            <Select
                                                placeholder="Select reporting timezone"
                                                className="w-full"
                                                options={IANA_TIMEZONE.map((tz) => ({ value: tz, label: tz }))}
                                            />
                                        </FormItem>
                                    </div>
                                </Card>
                            ),
                        },
                        {
                            key: 'features',
                            icon: <AppstoreOutlined />,
                            label: 'Features',
                            children: (
                                <div className="space-y-6">
                                    <Card>
                                        <FormItem name="enabledModules">
                                            <EnabledModulesField
                                                sections={[
                                                    { title: 'Enabled modules', options: moduleOptions },
                                                    { title: 'Enabled tools', options: toolOptions },
                                                ]}
                                            />
                                        </FormItem>
                                    </Card>
                                    <Card title="MCP access override">
                                        <div className="flex flex-col gap-3">
                                            <Typography.Text type="secondary">
                                                Force MCP access on or off for this organisation, or inherit the
                                                organisation's own setting. Forcing off immediately revokes the
                                                organisation's MCP tokens.
                                            </Typography.Text>
                                            <SegmentedControl
                                                value={data?.mcpSystemOverride ?? McpAccessSetting.INHERIT}
                                                onChange={(value) => setMcpOverride({ orgId, override: value })}
                                                disabled={isSavingMcpOverride}
                                                aria-label="MCP access override"
                                                options={[
                                                    { label: 'Inherit', value: McpAccessSetting.INHERIT },
                                                    { label: 'On', value: McpAccessSetting.ON },
                                                    { label: 'Off', value: McpAccessSetting.OFF },
                                                ]}
                                            />
                                        </div>
                                    </Card>
                                    <Card title="Demo organisation">
                                        <div className="flex items-center justify-between gap-4">
                                            <Typography.Text type="secondary">
                                                Mark this organisation as a demo org. Demo orgs are eligible for the
                                                demo-data tooling (generators and the ops MCP) and the demo-org cleanup
                                                paths. Keep this off for real tenants. Staging and development only.
                                            </Typography.Text>
                                            <Switch
                                                checked={data?.isDemoOrg ?? false}
                                                loading={isSavingDemoOrg}
                                                onChange={(checked) => setDemoOrg({ orgId, isDemoOrg: checked })}
                                            />
                                        </div>
                                    </Card>
                                    <Card title="Curium Lite">
                                        <div className="flex items-center justify-between gap-4">
                                            <Typography.Text type="secondary">
                                                Trial tier for this organisation. While enabled, effective modules are
                                                capped to Compliance regardless of the selection above, premium features
                                                show upgrade prompts, AI analyses are redacted to counts, and template
                                                settings are read-only.
                                            </Typography.Text>
                                            <Switch
                                                checked={data?.isLiteOrg ?? false}
                                                loading={isSavingLiteOrg}
                                                aria-label="Curium Lite"
                                                onChange={(checked) =>
                                                    setLiteOrg({ orgId, set: { isLiteOrg: checked } })
                                                }
                                            />
                                        </div>
                                    </Card>
                                    <Card title="Service API access">
                                        <div className="flex items-center justify-between gap-4">
                                            <Typography.Text type="secondary">
                                                Enable the Service API (/api/v1) for this organisation so it can mint
                                                and use service tokens for external integrations. Off by default — turn
                                                on per contract.
                                            </Typography.Text>
                                            <Switch
                                                checked={data?.serviceApiEnabled ?? false}
                                                loading={isSavingServiceApiEnabled}
                                                aria-label="Service API access"
                                                onChange={(checked) =>
                                                    setServiceApiEnabled({ orgId, set: { serviceApiEnabled: checked } })
                                                }
                                            />
                                        </div>
                                    </Card>
                                </div>
                            ),
                        },
                        {
                            key: 'authentication',
                            icon: <LockOutlined />,
                            label: 'Authentication',
                            children: (
                                <div className="space-y-6">
                                    <Card>
                                        <FormItem
                                            name="enabledAuth0Connections"
                                            label="Authentication Methods"
                                            tooltip={{
                                                title: 'Select the allowed authentication methods for this organisation',
                                                icon: <InfoCircleOutlined />,
                                            }}
                                        >
                                            <Select
                                                mode="multiple"
                                                placeholder="Select authentication methods"
                                                allowClear
                                                className="w-full"
                                                options={[
                                                    {
                                                        label: (
                                                            <span className="flex items-center gap-2">
                                                                <LockOutlined />
                                                                {AUTH0_CONNECTIONS.DATABASE.label}
                                                            </span>
                                                        ),
                                                        value: AUTH0_CONNECTIONS.DATABASE.name,
                                                    },
                                                    {
                                                        label: (
                                                            <span className="flex items-center gap-2">
                                                                <LockOutlined />
                                                                {AUTH0_CONNECTIONS.AZURE_AD.label}
                                                            </span>
                                                        ),
                                                        value: AUTH0_CONNECTIONS.AZURE_AD.name,
                                                    },
                                                ]}
                                            />
                                        </FormItem>
                                    </Card>

                                    <Card title="Azure AD Admin Consent" className="mt-6">
                                        <div className="space-y-4">
                                            <Alert
                                                title="Azure AD Admin Consent Setup"
                                                description="Generate an admin consent URL for your Azure AD tenant. This allows administrators to grant consent for all users in the organisation to use single sign-on with Curium."
                                                type="info"
                                                showIcon
                                                icon={<InfoCircleOutlined />}
                                            />

                                            <div>
                                                <Text strong>Instructions:</Text>
                                                <ol className="mt-2 ml-5 text-muted-foreground text-sm">
                                                    <li>
                                                        Enter your Azure AD tenant domain (e.g.,
                                                        "contoso.onmicrosoft.com" or "contoso.com")
                                                    </li>
                                                    <li>Click "Generate Admin Consent URL"</li>
                                                    <li>
                                                        Copy the generated URL and share it with your Azure AD
                                                        administrator
                                                    </li>
                                                    <li>The administrator must visit the URL and grant consent</li>
                                                    <li>After consent is granted, users can sign in using Azure AD</li>
                                                </ol>
                                            </div>

                                            <div className="flex gap-2">
                                                <Input
                                                    placeholder="Enter tenant domain (e.g., contoso.onmicrosoft.com)"
                                                    value={tenantDomain}
                                                    onChange={(e) => setTenantDomain(e.target.value)}
                                                    prefix={<Link2 size={14} />}
                                                    className="flex-1"
                                                />
                                                <Button
                                                    type="primary"
                                                    onClick={handleGenerateConsentUrl}
                                                    icon={<Link2 />}
                                                >
                                                    Generate Admin Consent URL
                                                </Button>
                                            </div>

                                            {showConsentUrl && generatedUrl && (
                                                <div className="mt-4 rounded-lg bg-muted p-4">
                                                    <div className="mb-2 flex items-start justify-between">
                                                        <Text strong>Generated Admin Consent URL:</Text>
                                                        <Button
                                                            type="tertiary"
                                                            size="small"
                                                            icon={<Copy />}
                                                            onClick={handleCopyUrl}
                                                        >
                                                            Copy URL
                                                        </Button>
                                                    </div>
                                                    <Paragraph
                                                        copyable={{
                                                            text: generatedUrl,
                                                            tooltips: ['Copy URL', 'URL Copied!'],
                                                        }}
                                                        className="mb-0 break-all rounded border border-separator bg-card p-2 text-xs"
                                                        code
                                                    >
                                                        {generatedUrl}
                                                    </Paragraph>
                                                    <Alert
                                                        title="Next Steps"
                                                        description="Share this URL with your Azure AD administrator. They will need to sign in with admin privileges and grant consent for your organisation."
                                                        type="success"
                                                        showIcon
                                                        className="mt-3"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </Card>
                                </div>
                            ),
                        },
                        {
                            key: 'email',
                            icon: <MailOutlined />,
                            label: 'Email Settings',
                            children: (
                                <div className="space-y-6">
                                    <Card>
                                        <EmailVerificationStatusPanel
                                            className="mb-6"
                                            status={emailVerificationStatus}
                                            isRechecking={isVerifyingEmailDomain}
                                            onRecheck={() => void handleRecheckEmailVerification()}
                                        />
                                        <div className="mb-6 flex justify-end">
                                            <SendTestEmailButton orgId={orgId} orgName={data?.name} />
                                        </div>
                                        <div className="grid gap-6 md:grid-cols-2">
                                            <FormItem
                                                label="Default email address (sender)"
                                                name="senderEmail"
                                                rules={[{ type: 'email' }]}
                                                tooltip={{
                                                    title: data?.isEmailDomainVerified
                                                        ? 'Domain is verified - emails will be sent from this address'
                                                        : 'The default email address used for sending emails. If domain is not verified, emails will be sent from info@curium.app',
                                                    icon: <InfoCircleOutlined />,
                                                }}
                                                help="The default email address used for sending organisation emails."
                                            >
                                                <Input placeholder="Enter default email address" type="email" />
                                            </FormItem>
                                            <NoReplyEmailField
                                                senderEmail={
                                                    watchedSenderEmail === undefined
                                                        ? data?.senderEmail
                                                        : watchedSenderEmail
                                                }
                                                savedSenderEmail={data?.senderEmail}
                                                authenticatedDomain={data?.authenticatedDomain}
                                                isAuthenticatedDomainVerified={data?.domainAuthStatus === 'verified'}
                                                savedNoReplyEmail={data?.noReplyEmail}
                                            />
                                            <AdminInboundReplyToField
                                                savedPlatformReplyEmail={data?.platformReplyEmail}
                                                isDomainAuthVerified={data?.domainAuthStatus === 'verified'}
                                            />
                                            <FormItem
                                                label="Authenticated Domain"
                                                name="authenticatedDomain"
                                                tooltip={{
                                                    title: 'The authenticated domain for sending emails. This field is automatically populated when domain authentication is configured.',
                                                    icon: <InfoCircleOutlined />,
                                                }}
                                            >
                                                <Input
                                                    placeholder="Automatically set via domain authentication"
                                                    disabled
                                                />
                                            </FormItem>
                                        </div>
                                    </Card>
                                    <DomainAuthSetupWithLiveReplyTo orgId={orgId} />
                                </div>
                            ),
                        },
                        ...(canManageOrganisations
                            ? [
                                  {
                                      key: 'integrations',
                                      icon: <ApiOutlined />,
                                      label: 'Integrations',
                                      children: (
                                          <IntegrationsSection orgId={orgId} reportTimezone={data?.reportTimezone} />
                                      ),
                                  },
                              ]
                            : []),
                        {
                            key: 'ai-digest',
                            icon: <ThunderboltOutlined />,
                            label: 'AI digest',
                            children: <DigestSettingsSection orgId={orgId} />,
                        },
                        {
                            key: 'stats',
                            icon: <BarChartOutlined />,
                            label: 'Stats',
                            children: <OrgStatsSection orgId={orgId} />,
                        },
                    ]}
                />
            </Form>

            {/* Import Modals */}
            <ImportUsersModal
                open={openImportModal === 'users'}
                orgId={orgId}
                onClose={() => setOpenImportModal(null)}
            />

            <ImportHandlingPartiesModal
                open={openImportModal === 'handlingParties'}
                orgId={orgId}
                onClose={() => setOpenImportModal(null)}
            />

            <ImportTrainingRecordsModal
                open={openImportModal === 'trainingRecords'}
                orgId={orgId}
                onClose={() => setOpenImportModal(null)}
            />

            <ImportIncidentsModal
                open={openImportModal === 'incidents'}
                orgId={orgId}
                onClose={() => setOpenImportModal(null)}
            />

            <ImportComplaintsModal
                open={openImportModal === 'complaints'}
                orgId={orgId}
                onClose={() => setOpenImportModal(null)}
            />

            <ImportRisksModal
                open={openImportModal === 'risks'}
                orgId={orgId}
                onClose={() => setOpenImportModal(null)}
            />

            <ImportRiskAppetiteStatementsModal
                open={openImportModal === 'riskAppetiteStatements'}
                orgId={orgId}
                onClose={() => setOpenImportModal(null)}
            />

            <ImportControlsModal
                open={openImportModal === 'controls'}
                orgId={orgId}
                onClose={() => setOpenImportModal(null)}
            />

            <ImportControlTestsModal
                open={openImportModal === 'controlTests'}
                orgId={orgId}
                onClose={() => setOpenImportModal(null)}
            />

            <ImportControlActivitiesModal
                open={openImportModal === 'controlActivities'}
                orgId={orgId}
                onClose={() => setOpenImportModal(null)}
            />
        </PageController>
    );
}

// ── Inbound Reply-To Field (reads live authenticatedDomain from admin form) ─

function AdminInboundReplyToField({
    savedPlatformReplyEmail,
    isDomainAuthVerified,
}: {
    savedPlatformReplyEmail?: string | null;
    isDomainAuthVerified?: boolean;
}) {
    // `hasAccess` → `admin_system:read` or `admin_system:full` — enough to query the registered
    // inbound-parse hostnames. `canEdit` → `admin_system:full`, which is the same gate the admin
    // `updateOrg` mutation uses to decide whether to auto-provision the hostname on save, so we
    // only promise auto-registration copy to users whose save would actually do it.
    const { hasAccess: hasSystemAccess, canEdit: canManageSystem } = useAdminModulePermissions('system');
    const authenticatedDomain = Form.useWatch<string | null | undefined>('authenticatedDomain');
    return (
        <InboundReplyToField
            availableDomainsQuery={hasSystemAccess ? 'admin' : 'none'}
            authenticatedDomain={authenticatedDomain}
            isDomainAuthVerified={isDomainAuthVerified}
            canAutoRegisterOnSave={canManageSystem}
            savedPlatformReplyEmail={savedPlatformReplyEmail}
        />
    );
}

// ── DomainAuthSetup wrapper that passes live form value ─────────────────────

function DomainAuthSetupWithLiveReplyTo({ orgId }: { orgId: bigint }) {
    // `admin.inboundParse.verifyMx` is gated on `admin_system:read`, but this page only
    // requires `admin_orgs:read`, so suppress the admin-only MX query (and its synthetic row)
    // when the viewer lacks system access to avoid a forever-pending status driven by 403s.
    const { hasAccess: hasSystemAccess } = useAdminModulePermissions('system');
    const platformReplyEmail = Form.useWatch<string | undefined>('platformReplyEmail');

    return (
        <DomainAuthSetup
            mode="admin"
            orgId={orgId}
            livePlatformReplyEmail={platformReplyEmail}
            canVerifyInboundParseMx={hasSystemAccess}
        />
    );
}
